import { useCallback, useEffect, useRef } from 'react';

/** How long after a wheel turn or touch drag a scroll still counts as the user's. */
const USER_SCROLL_WINDOW_MS = 600;

/**
 * Tells scrolls the user makes apart from ones the browser makes on its own.
 *
 * Focusing an input near the bottom of the screen on a tablet makes Safari
 * scroll the calendar to bring it into view (and again to clear the
 * on-screen keyboard). Pickers that close "when the calendar scrolls" would
 * otherwise close the moment they open.
 *
 * @returns {Function} Returns true when a wheel turn or touch drag happened
 * within the last USER_SCROLL_WINDOW_MS.
 */
const useUserScrollIntent = () => {
  const lastIntentAtRef = useRef(0);

  useEffect(() => {
    const markIntent = () => {
      lastIntentAtRef.current = Date.now();
    };
    const options = { capture: true, passive: true };
    window.addEventListener('wheel', markIntent, options);
    window.addEventListener('touchmove', markIntent, options);
    return () => {
      window.removeEventListener('wheel', markIntent, options);
      window.removeEventListener('touchmove', markIntent, options);
    };
  }, []);

  return useCallback(
    () => Date.now() - lastIntentAtRef.current < USER_SCROLL_WINDOW_MS,
    [],
  );
};

export default useUserScrollIntent;
